import React, { useState, useContext } from 'react';
import { Input, Button, Select, message, Modal } from 'antd';
import {
  UserOutlined,
  BellOutlined,
  RobotOutlined,
  ApiOutlined,
  LogoutOutlined,
  BgColorsOutlined,
} from '@ant-design/icons';
import { Navigate, useSearchParams } from 'react-router-dom';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../contexts/company/CompanyContext';
import PageScaffold from '../../components/PageScaffold';
import ThemeToggle from '../../components/@shared/ThemeToggle';
import { useProfileImage } from '../../hooks/profile/useProfileImage';
// TODO: @MrWarzo checker formatPhoneNumberDisplay et le remplacer
import { formatPhoneNumberDisplay } from '../../../utils/phone';
import './SettingsPage.css';
import classNames from 'classnames';
import { COMPANY_BILLING_PAGE_PATH } from '../company/CompanyBillingPage';
import { COMPANY_SETTINGS_PAGE_PATH } from '../company/CompanySettingsPage';

export const SETTINGS_PAGE_PATH = '/settings';
export const SETTINGS_PAGE_TITLE = 'Paramètres';

// Valid query param values for the "page" search param
const VALID_TABS = [
  'informations', 
  'alertes', 
  'ia', 
  'passerelle', 
  // 'apparence'
] as const;

// Types
type SettingsTab = (typeof VALID_TABS)[number];

interface NavItem {
  key: SettingsTab;
  label: string;
  icon: React.ReactNode;
  comingSoon?: boolean;
}

const navItems: NavItem[] = [
  { key: 'informations', label: 'Mes informations', icon: <UserOutlined /> },
  // { key: 'apparence', label: 'Apparence', icon: <BgColorsOutlined /> },
  { key: 'alertes', label: 'Alertes', icon: <BellOutlined />, comingSoon: true },
  { key: 'ia', label: 'Intelligence Artificielle', icon: <RobotOutlined />, comingSoon: true },
  {
    key: 'passerelle',
    label: 'Passerelle métier',
    icon: <ApiOutlined />,
    comingSoon: true,
  },
];

// A member only manages their own profile
const MEMBER_TABS: SettingsTab[] = ['informations'];

// Former tabs, moved under "Mon entreprise": old links and bookmarks still land on them
const MOVED_TABS: Record<string, string> = {
  facturation: COMPANY_BILLING_PAGE_PATH,
  api: COMPANY_SETTINGS_PAGE_PATH,
};

interface SettingsPageProps {}

const SettingsPage: React.FC<SettingsPageProps> = () => {
  const {
    submitSignOut,
    userDoc,
    updateUserProfile,
    isUpdatingProfile,
    sendPasswordReset,
    isSendingPasswordReset,
  } = useContext(AuthenticationContext);
  const { activeCompany } = useContext(CompanyContext);
  const { pickImage } = useProfileImage();
  const [searchParams, setSearchParams] = useSearchParams();

  const visibleNavItems = activeCompany?.isSupervisor
    ? navItems
    : navItems.filter((item) => MEMBER_TABS.includes(item.key));

  // Derive active tab from the "page" query param, default to 'informations'
  const pageParam = searchParams.get('page') as SettingsTab | null;
  const activeTab: SettingsTab =
    pageParam && visibleNavItems.some((item) => item.key === pageParam) ? pageParam : 'informations';

  /**
   * Update the active tab by updating the "page" search param.
   * If the tab is 'informations' (default), we remove the param to keep the URL clean.
   */
  const setActiveTab = (tab: SettingsTab) => {
    if (tab === 'informations') {
      searchParams.delete('page');
    } else {
      searchParams.set('page', tab);
    }
    setSearchParams(searchParams, { replace: true });
  };
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    timezone: 'europe-paris',
    avatar: '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);

  // Reseme le formulaire depuis le document utilisateur pendant le rendu
  // (pattern « ajuster l'etat quand une prop change » de la doc React) : dans un
  // effet, l'arrivee du document coutait un second rendu de la page.
  // Starts empty so a document already loaded at mount still fills the form
  const [lastUserDoc, setLastUserDoc] = useState<typeof userDoc | undefined>(undefined);
  if (userDoc !== lastUserDoc) {
    setLastUserDoc(userDoc);
    if (userDoc) {
      setFormData({
        nom: userDoc.lastname,
        prenom: userDoc.firstname,
        email: userDoc.email,
        telephone: userDoc.phone,
        timezone: 'europe-paris',
        avatar: userDoc.avatar,
      });
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleImagePick = () => {
    pickImage((imageUri) => {
      setFormData((prev) => ({ ...prev, avatar: imageUri }));
      setHasChanges(true);
    });
  };

  const handleSaveProfile = async () => {
    if (!hasChanges || isUpdatingProfile) return;

    const result = await updateUserProfile({
      firstname: formData.prenom,
      lastname: formData.nom,
      avatar: formData.avatar,
    });

    if (result.success) {
      message.success('Profil mis à jour avec succès');
      setHasChanges(false);
    } else {
      message.error(result.error || 'Erreur lors de la mise à jour du profil');
    }
  };

  const handlePasswordResetClick = () => {
    setIsPasswordModalVisible(true);
  };

  const handlePasswordResetConfirm = async () => {
    if (isSendingPasswordReset) return;

    const result = await sendPasswordReset();

    if (result.success) {
      message.success('Un email de réinitialisation a été envoyé à votre adresse email');
      setIsPasswordModalVisible(false);
    } else {
      message.error(result.error || "Erreur lors de l'envoi de l'email");
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'informations':
        return (
          <>
            <div className="settings-page__content-header">
              <h1 className="settings-page__content-title">Informations personnelles</h1>
              <p className="settings-page__content-subtitle">
                Cet espace vous permet de gérer et modifier vos informations personnelles.
              </p>
            </div>

            {/* Photo section */}
            <div className="settings-page__photo-section">
              <div className="settings-page__photo-label">Photo de profil</div>
              <div className="settings-page__photo-row">
                <img
                  src={formData.avatar || 'https://via.placeholder.com/100'}
                  alt="Profile"
                  className="settings-page__photo-avatar"
                />
                <Button className="settings-page__photo-button" onClick={handleImagePick}>
                  Modifier la photo
                </Button>
                <span className="settings-page__photo-hint">PNG, JPG, maximum 1MB</span>
              </div>
            </div>

            {/* Form */}
            <div className="settings-page__form">
              <div className="settings-page__form-row">
                <div className="settings-page__form-group">
                  <label className="settings-page__form-label">Prénom</label>
                  <Input
                    className="settings-page__form-input"
                    value={formData.prenom}
                    onChange={(e) => handleInputChange('prenom', e.target.value)}
                  />
                </div>
                <div className="settings-page__form-group">
                  <label className="settings-page__form-label">Nom</label>
                  <Input
                    className="settings-page__form-input"
                    value={formData.nom}
                    onChange={(e) => handleInputChange('nom', e.target.value)}
                  />
                </div>
              </div>

              <div className="settings-page__form-row">
                <div className="settings-page__form-group">
                  <label className="settings-page__form-label">Email</label>
                  <Input className="settings-page__form-input" value={formData.email} disabled />
                  <span className="settings-page__form-hint">
                    Contactez le support pour modifier l'email
                  </span>
                </div>
                <div className="settings-page__form-group">
                  <label className="settings-page__form-label">Téléphone</label>
                  <Input
                    className="settings-page__form-input"
                    value={formatPhoneNumberDisplay(formData.telephone)}
                    disabled
                  />
                  <span className="settings-page__form-hint">
                    Contactez le support pour modifier votre numéro
                  </span>
                </div>
              </div>

              <div className="settings-page__form-row">
                <div className="settings-page__form-group">
                  <label className="settings-page__form-label">Mot de passe</label>
                  <Button
                    className="settings-page__password-button"
                    onClick={handlePasswordResetClick}
                  >
                    Changer le mot de passe
                  </Button>
                </div>
                <div className="settings-page__form-group" style={{ display: 'none' }}>
                  <label className="settings-page__form-label">Fuseau horaire</label>
                  <Select
                    className="settings-page__form-select"
                    value={formData.timezone}
                    onChange={(value) => handleInputChange('timezone', value)}
                    style={{ width: '100%' }}
                    options={[
                      { value: 'europe-paris', label: '(GMT+1) Paris' },
                      { value: 'europe-london', label: '(GMT+0) London' },
                      { value: 'europe-berlin', label: '(GMT+1) Berlin' },
                      { value: 'america-new-york', label: '(GMT-5) New York' },
                    ]}
                  />
                </div>
              </div>

              {/* Save button */}
              <div className="settings-page__form-actions">
                <Button
                  type="primary"
                  className="settings-page__save-button"
                  onClick={handleSaveProfile}
                  loading={isUpdatingProfile}
                  disabled={!hasChanges || isUpdatingProfile}
                >
                  Enregistrer les modifications
                </Button>
              </div>
            </div>
          </>
        );
      // case 'apparence':
      //   return (
      //     <>
      //       <div className="settings-page__content-header">
      //         <h1 className="settings-page__content-title">Apparence</h1>
      //         <p className="settings-page__content-subtitle">
      //           Personnalisez l'apparence de votre interface.
      //         </p>
      //       </div>
      //       <ThemeToggle />
      //     </>
      //   );
      default:
        return (
          <div className={"settings-page__content-header"}>
            <h1 className="settings-page__content-title">
              {navItems.find((item) => item.key === activeTab)?.label}
            </h1>
            <p className="settings-page__content-subtitle">
              Cette section est en cours de développement.
            </p>
          </div>
        );
    }
  };

  const movedTabPath = MOVED_TABS[searchParams.get('page') ?? ''];
  if (movedTabPath) return <Navigate to={movedTabPath} replace />;

  return (
    <PageScaffold title={SETTINGS_PAGE_TITLE} noPadding hideHeader>
      <div className="settings-page">
        {/* Sidebar */}
        <div className="settings-page__sidebar">
          <div className="settings-page__sidebar-profile">
            <img
              src={formData.avatar || 'https://via.placeholder.com/100'}
              alt="Profile"
              className="settings-page__sidebar-avatar"
            />
            <span className="settings-page__sidebar-name">
              {formData.prenom} {formData.nom}
            </span>
            <span className="settings-page__sidebar-email">{formData.email}</span>
          </div>

          <nav className="settings-page__sidebar-nav">
            {visibleNavItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={classNames('settings-page__sidebar-nav-item', item.comingSoon ? 'settings-page__sidebar-nav-item--comingsoon' : '')}
                onClick={() => setActiveTab(item.key)}
              >
                <span className="settings-page__sidebar-nav-icon">{item.icon}</span>
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <button type="button" className="settings-page__sidebar-logout" onClick={submitSignOut}>
            <span className="settings-page__sidebar-nav-icon">
              <LogoutOutlined />
            </span>
            <span>Se déconnecter</span>
          </button>
        </div>

        {/* Content */}
        <div className="settings-page__content">{renderContent()}</div>
      </div>

      <Modal
        title="Changer le mot de passe"
        open={isPasswordModalVisible}
        onCancel={() => setIsPasswordModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsPasswordModalVisible(false)}>
            Annuler
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isSendingPasswordReset}
            onClick={handlePasswordResetConfirm}
          >
            Confirmer
          </Button>,
        ]}
      >
        <p>Un email va vous être envoyé pour changer votre mot de passe.</p>
      </Modal>
    </PageScaffold>
  );
};

export default SettingsPage;
